import React, { useRef, useState } from "react";
import { useAutoGrow } from "../lib/autogrow";

// Controlled auto-growing textarea (max 4 lines) with @mention autocomplete.
// props: value, onChange(str), members[], as ('input'|'textarea'), placeholder,
//        onEnter (called on Enter when as='input' & dropdown closed), className, style, rows, testid, wrapperClassName
export function MentionInput({
  value, onChange, members = [], as = "input", placeholder,
  onEnter, className, style, rows = 1, testid = "mention-input", wrapperClassName = "",
}) {
  const ref = useRef(null);
  const [query, setQuery] = useState(null);
  const [active, setActive] = useState(0);
  useAutoGrow(ref, value, 4);

  const detect = (val, caret) => {
    const upto = val.slice(0, caret);
    const m = upto.match(/(?:^|\s)@([\p{L}\d'’.-]*)$/u);
    setQuery(m ? m[1].toLowerCase() : null);
    setActive(0);
  };

  const handleChange = (e) => { onChange(e.target.value); detect(e.target.value, e.target.selectionStart); };

  const matches = query != null
    ? members.filter((mm) => (mm.name || "").toLowerCase().includes(query)).slice(0, 6)
    : [];

  const pick = (mm) => {
    const el = ref.current;
    const caret = el ? el.selectionStart : value.length;
    const upto = value.slice(0, caret);
    const idx = upto.lastIndexOf("@");
    if (idx < 0) return;
    const before = value.slice(0, idx);
    const after = value.slice(caret);
    const inserted = `@${mm.name} `;
    const newVal = before + inserted + after;
    onChange(newVal);
    setQuery(null);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = (before + inserted).length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleKeyDown = (e) => {
    if (matches.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % matches.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + matches.length) % matches.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(matches[active]); return; }
      if (e.key === "Escape") { setQuery(null); return; }
    }
    // In single-line ('input') mode Enter submits (no newline); in 'textarea' mode Enter adds a newline.
    if (e.key === "Enter" && as === "input" && !e.shiftKey) { e.preventDefault(); onEnter && onEnter(); }
  };

  const common = {
    ref, value, rows, onChange: handleChange, onKeyDown: handleKeyDown,
    placeholder, className: `${className || ""} resize-none`, style: { ...style, maxHeight: "6.5rem" },
    "data-testid": testid,
    onBlur: () => setTimeout(() => setQuery(null), 150),
  };

  return (
    <div className={`relative ${wrapperClassName}`}>
      <textarea {...common} />
      {matches.length > 0 && (
        <div className="absolute left-0 right-0 z-[80] mt-1 rounded-xl overflow-hidden shadow-xl"
          style={{ bottom: as === "input" ? "calc(100% + 4px)" : "auto", background: "var(--bg-elevated)", border: "1px solid var(--border)" }}
          data-testid={`${testid}-dropdown`}>
          {matches.map((mm, i) => (
            <button key={mm.user_id || mm.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(mm); }}
              data-testid={`${testid}-option-${i}`}
              className="w-full flex items-center gap-2 px-3 py-2 text-left"
              style={{ background: i === active ? "var(--overlay)" : "transparent" }}>
              <span className="h-7 w-7 rounded-full grid place-items-center text-xs font-bold shrink-0"
                style={{ background: "color-mix(in srgb, var(--accent) 18%, transparent)", color: "var(--accent)" }}>
                {(mm.name || "?").charAt(0).toUpperCase()}
              </span>
              <span className="text-sm" style={{ color: "var(--text-primary)" }}>{mm.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
